/**
 * @file Turns a loaded character `.glb` (skinned mesh + skeleton + idle/run
 * clips, no material) plus its skin texture into a game-ready, animated body.
 *
 * The source pack ships a bare rig and a set of separate diffuse textures —
 * unlike the weapon pack, there is no material to re-tint, so preparation here
 * is "paint the skin on" rather than the weapons' "re-tint the grey".
 *
 * Pivots: the rig's root sits at the character's feet, which is the wrong
 * convention for this renderer — every other prefab (the capsule this
 * replaces included) is centred on its own origin, and RenderSystem lifts it
 * onto the floor by `Renderable.yOffset`. So the body is re-centred here to
 * match, same as {@link ./WeaponModels.js} centres its models.
 *
 * The library starts empty for the same reason {@link ./WeaponModels.js} does:
 * prefabs are declared before the model/texture pair finishes downloading, and
 * a body built early would bake the procedural-capsule fallback in for good
 * (see the `prewarm: 0` note on the player prefab in Prefabs.js).
 */

import * as THREE from 'three';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { CHARACTER_ART, CHARACTER_MODEL_SCALE, characterModelKey, characterSkinKey } from '../config/CharacterArt.js';
import { WEAPON_MOUNT } from './Prefabs.js';

/**
 * Bone a held weapon rides on. Every skin in the pack shares this rig, so the
 * name is fixed rather than configured per entity.
 */
const HAND_BONE = 'RightHand';

/**
 * Grip position, in the hand bone's local space, at rest (idle/run) versus at
 * full aim blend (see {@link poseAiming}) — {@link CharacterAnimationSystem}
 * lerps between the two by `aim.blend` every frame, same as it lerps the arm
 * itself, rather than holding the gun in one fixed spot regardless of pose.
 * Both started from a bare nudge off the wrist and were walked in against a
 * real screenshot: idle needed to drop lower to sit in the hand rather than
 * float above it, and firing needed to push further forward along the same
 * solved facing direction so the arm's extension carries the gun with it.
 */
const HAND_GRIP_POSITION = Object.freeze({
  idle: [-0.111, -0.02, 0.039],
  // Pushed forward another 20% of the pistol's own length (measured off its
  // world bounding box, ~0.32 units long) along the mount's solved forward
  // axis, converted into this hand-bone-local frame.
  firing: [-0.2449, 0.1498, 0.0288]
});

/** Crossfade time between idle and run, in seconds. */
const ANIMATION_FADE_SECONDS = 0.15;

/** Speed, in units/second, above which a body is considered "running". */
const RUN_SPEED_THRESHOLD = 0.5;

/**
 * Right-arm bones the aim pose extends while firing. The pack ships no
 * aim/fire clip, so this is solved procedurally rather than authored — see
 * {@link poseAiming}.
 */
const RIGHT_SHOULDER_BONE = 'RightShoulder';
const RIGHT_UPPER_ARM_BONE = 'RightArm';
const RIGHT_FOREARM_BONE = 'RightForeArm';

/**
 * Every limb bone in this rig points its local +Y axis at its child (checked
 * against the rest pose: shoulder->elbow and elbow->hand both come out as
 * local (0,1,0)), so aiming the arm is just solving for the local rotation
 * that sends +Y toward {@link AIM_DIRECTION}.
 */
const LIMB_CHILD_AXIS = new THREE.Vector3(0, 1, 0);

/**
 * Direction the extended arm points at full aim blend, in the body's own
 * local space (i.e. before its facing rotation is applied) — mostly forward,
 * a little down, level with where a pistol grip naturally sits.
 */
const AIM_DIRECTION = new THREE.Vector3(0, -0.15, 1).normalize();

/** How fast the arm approaches its target aim blend, in 1/second. */
const AIM_BLEND_RATE = 10;

// Scratch objects for poseAiming: it runs once a frame for one character, but
// the no-allocation-mid-frame rule (GDD §19) still applies.
const _worldDir = new THREE.Vector3();
const _targetWorldQuat = new THREE.Quaternion();
const _parentWorldQuat = new THREE.Quaternion();
const _straight = new THREE.Quaternion();
const _idleGripPosition = new THREE.Vector3(...HAND_GRIP_POSITION.idle);
const _firingGripPosition = new THREE.Vector3(...HAND_GRIP_POSITION.firing);
const _characterWorldQuat = new THREE.Quaternion();

export class CharacterModelLibrary {
  constructor() {
    /**
     * Entity key → prepared source (template object + its animation clips).
     * @type {Map<string, { object: THREE.Object3D, clips: THREE.AnimationClip[] }>}
     * @private
     */
    this._sources = new Map();
  }

  /**
   * Takes the models/textures an {@link ../core/AssetLoader.js AssetLoader}
   * has fetched and prepares one skinned, lit source template per entity.
   * Safe to call on every floor: entities already prepared are left alone.
   *
   * @param {import('../core/AssetLoader.js').AssetLoader} assets Loader holding
   *   the assets from {@link ../config/CharacterArt.js characterModelManifest}.
   * @returns {void}
   */
  adopt(assets) {
    for (const [key, art] of Object.entries(CHARACTER_ART)) {
      if (this._sources.has(key)) continue;

      const scene = assets.get(characterModelKey(key));
      const skin = assets.get(characterSkinKey(key));
      if (!scene || !skin) continue;

      this._sources.set(key, prepare(scene, skin, art));
    }
  }

  /**
   * @param {string} key Character entity key.
   * @returns {boolean} Whether a model is available for it.
   */
  has(key) {
    return this._sources.has(key);
  }

  /**
   * Builds a fresh, independently animatable instance of an entity's body.
   * Geometry, skeleton shape and material are shared with every other
   * instance; `SkeletonUtils.clone` is what makes that safe — a plain
   * `Object3D.clone()` on a `SkinnedMesh` copies a *reference* to the source's
   * `Skeleton` instead of cloning it (see three.js's `SkinnedMesh.copy()`),
   * so every clone would drive its mesh from bones that live outside the
   * actual scene graph and never receive a `matrixWorld` update — which reads
   * as the model rendering as a huge, wrongly-deformed blob, not a body.
   *
   * @param {string} key Character entity key.
   * @returns {THREE.Object3D | null} A group whose own transform is untouched,
   *   so a caller may set position/rotation/scale on it freely. Its
   *   `userData.animation` holds `{ mixer, actions, current }` for
   *   {@link ../systems/CharacterAnimationSystem.js} to drive every frame, and
   *   its weapon socket (name `WEAPON_MOUNT`) already rides the hand bone.
   *   Null when no model is ready yet, which is the caller's cue to fall back.
   */
  create(key) {
    const source = this._sources.get(key);
    if (!source) return null;

    const object = cloneSkinned(source.object);

    const mixer = new THREE.AnimationMixer(object);
    const actions = {};
    for (const clip of source.clips) {
      actions[clip.name.toLowerCase()] = mixer.clipAction(clip);
    }
    const idle = actions.idle;
    idle?.play();

    object.userData.animation = { mixer, actions, current: idle ?? null };

    let mount = null;
    const handBone = object.getObjectByName(HAND_BONE);
    if (handBone) {
      mount = new THREE.Group();
      mount.name = WEAPON_MOUNT;
      mount.position.set(...HAND_GRIP_POSITION.idle);
      // Rotation isn't set here: the hand's own world orientation shifts
      // between idle sway and the extended firing pose, so a fixed value
      // solved for one goes wrong in the other (the gun tipped to point at
      // the floor while firing). `poseAiming` re-solves it every frame
      // instead, against whatever pose the hand is actually in right now.
      handBone.add(mount);
    }

    const shoulderBone = object.getObjectByName(RIGHT_SHOULDER_BONE);
    const upperArmBone = object.getObjectByName(RIGHT_UPPER_ARM_BONE);
    const foreArmBone = object.getObjectByName(RIGHT_FOREARM_BONE);
    object.userData.animation.aim =
      mount && shoulderBone && upperArmBone && foreArmBone
        ? { mount, handBone, shoulderBone, upperArmBone, foreArmBone, blend: 0 }
        : null;

    return object;
  }

  /** @returns {void} */
  clear() {
    this._sources.clear();
  }
}

/**
 * @param {THREE.Object3D} scene Loaded glTF scene (skinned mesh + skeleton),
 *   with `scene.animations` set by {@link ../core/AssetLoader.js}.
 * @param {THREE.Texture} skin Diffuse texture for this entity.
 * @param {import('../config/CharacterArt.js').CharacterArt} art Art entry.
 * @returns {{ object: THREE.Object3D, clips: THREE.AnimationClip[] }} Template
 *   for {@link CharacterModelLibrary#create} to clone.
 */
function prepare(scene, skin, art) {
  skin.colorSpace = THREE.SRGBColorSpace;

  const material = new THREE.MeshStandardMaterial({
    map: skin,
    // No emissive: the character reads by its own skin texture under normal
    // scene lighting, not a neon glow — unlike the rest of the cast, it isn't
    // a flat procedural colour that needs one to stay legible.
    roughness: 0.6,
    metalness: 0.05,
    side: THREE.DoubleSide
  });

  scene.traverse((node) => {
    if (!node.isMesh) return;
    node.castShadow = true;
    node.receiveShadow = false;
    // The FBX->glTF conversion bakes a chain of axis-correction rotations
    // onto this node (see scripts/convert-character.mjs); three's default
    // frustum culling gets the transformed bounding sphere for a SkinnedMesh
    // wrong through that chain and drops every triangle as off-screen even
    // when it's dead centre. Skipping the cull check is far cheaper than
    // fighting the export pipeline for one on-screen body.
    node.frustumCulled = false;
    node.material = material;
  });

  // The rig's root sits at the character's feet; the render pivot convention
  // everywhere else (see the capsule this replaces) is "origin at body
  // centre, RenderSystem lifts it onto the floor by yOffset". Re-centre here
  // rather than special-case the render offset per prefab.
  const bounds = new THREE.Box3().setFromObject(scene);
  const centre = bounds.getCenter(new THREE.Vector3());

  const inner = new THREE.Group();
  inner.add(scene);
  scene.position.sub(centre);
  inner.rotation.y = art.rotationY ?? 0;
  inner.scale.setScalar(art.scale ?? CHARACTER_MODEL_SCALE);

  const wrapper = new THREE.Group();
  wrapper.name = 'character-model';
  wrapper.add(inner);
  return { object: wrapper, clips: scene.animations ?? [] };
}

/**
 * Slerps the right arm from whatever the idle/run clip just posed it into,
 * toward a straight, forward-extended aim pose — and back, when not firing.
 * Call once a frame, after `mixer.update()` has set this frame's base pose.
 *
 * @param {THREE.Object3D} body The character wrapper returned by
 *   {@link CharacterModelLibrary#create} (its facing rotates the aim
 *   direction with it).
 * @param {number} targetBlend 0 (pure animated pose) to 1 (fully extended).
 * @param {number} dt Delta time in seconds.
 * @returns {void}
 */
export function poseAiming(body, targetBlend, dt) {
  const aim = body.userData.animation?.aim;
  if (!aim) return;

  aim.blend += (targetBlend - aim.blend) * Math.min(1, AIM_BLEND_RATE * dt);
  aim.mount.position.lerpVectors(_idleGripPosition, _firingGripPosition, aim.blend);

  // `getWorldQuaternion` needs this frame's matrices, which the mixer's
  // local-only writes don't produce on their own.
  body.updateMatrixWorld(true);
  body.getWorldQuaternion(_characterWorldQuat);

  if (aim.blend >= 0.001 || targetBlend !== 0) {
    _worldDir.copy(AIM_DIRECTION).applyQuaternion(_characterWorldQuat).normalize();
    _targetWorldQuat.setFromUnitVectors(LIMB_CHILD_AXIS, _worldDir);

    aim.shoulderBone.getWorldQuaternion(_parentWorldQuat);
    _parentWorldQuat.invert().multiply(_targetWorldQuat);
    aim.upperArmBone.quaternion.slerp(_parentWorldQuat, aim.blend);

    // The forearm continues the same world direction as the now-aimed upper
    // arm — a straight arm — which relative to its (just-aimed) parent is
    // simply no additional rotation at all.
    aim.foreArmBone.quaternion.slerp(_straight.identity(), aim.blend);
  }

  // The gun always points the way the body is facing, regardless of what
  // pose the hand actually ends up in (idle sway, running, fully extended
  // aim) — solved live every frame instead of once against a fixed pose,
  // which is what let the barrel tip down as soon as the arm extended to
  // fire: the offset solved for idle stopped being correct the moment the
  // hand's own orientation changed. Must run *after* the arm slerp above and
  // re-propagate matrices, or it reads the hand's pre-extension orientation
  // from before this frame's pose was applied.
  body.updateMatrixWorld(true);
  aim.handBone.getWorldQuaternion(_parentWorldQuat);
  aim.mount.quaternion.copy(_parentWorldQuat.invert().multiply(_characterWorldQuat));
}

export { HAND_BONE, ANIMATION_FADE_SECONDS, RUN_SPEED_THRESHOLD };
